import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

// --- Helper Functions (Pure & Reusable) ---

const formatDate = (dateStr) => {
  if (!dateStr) return new Date().toLocaleDateString("fr-MA");
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return new Date().toLocaleDateString("fr-MA");
    return date.toLocaleDateString("fr-MA");
  } catch (e) {
    console.error("Error formatting date:", e);
    return new Date().toLocaleDateString("fr-MA");
  }
};

const formatNumber = (value) => {
  const numValue = parseFloat(value) || 0;
  return numValue
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    .replace(".", ",");
};

const formatCurrency = (value) => formatNumber(value);

const formatPercent = (value) => {
  const num = Number(value) || 0;
  // Fix floating point precision issues
  const rounded = Math.round(num * 100) / 100;
  return `${rounded.toFixed(0)}%`;
};

const convertToWords = (num) => {
  const units = [
    "",
    "un",
    "deux",
    "trois",
    "quatre",
    "cinq",
    "six",
    "sept",
    "huit",
    "neuf",
    "dix",
    "onze",
    "douze",
    "treize",
    "quatorze",
    "quinze",
    "seize",
    "dix-sept",
    "dix-huit",
    "dix-neuf",
  ];
  const tens = [
    "",
    "",
    "vingt",
    "trente",
    "quarante",
    "cinquante",
    "soixante",
    "soixante",
    "quatre-vingt",
    "quatre-vingt",
  ];
  const scale = ["", "mille", "million", "milliard", "billion"];

  num = parseFloat(num) || 0;
  if (num === 0) return "zéro";

  const numberToWords = (n) => {
    if (n < 20) return units[n];
    if (n < 100) {
      const unit = n % 10;
      const ten = Math.floor(n / 10);
      let word = tens[ten];
      if (ten === 7 || ten === 9) {
        word += unit > 0 ? "-" + units[10 + unit] : "";
      } else {
        if (unit === 1 && (ten === 7 || ten === 8)) word += "-et-un";
        else if (unit > 0) word += "-" + units[unit];
      }
      return word;
    }
    if (n < 1000) {
      const hundred = Math.floor(n / 100);
      const remainder = n % 100;
      const hundredWord = hundred > 1 ? units[hundred] + " cent" : "cent";
      return (
        hundredWord + (remainder > 0 ? " " + numberToWords(remainder) : "")
      );
    }

    let i = 0;
    let words = "";
    do {
      const chunk = n % 1000;
      if (chunk !== 0) {
        let chunkWords = numberToWords(chunk);
        if (i === 1 && chunk === 1) {
          chunkWords = "mille";
        } else if (i > 0) {
          chunkWords += " " + scale[i];
          if (chunk > 1 && i > 1) {
            chunkWords += "s";
          }
        }
        words = chunkWords + (words ? " " + words : "");
      }
      n = Math.floor(n / 1000);
      i++;
    } while (n > 0);
    return words;
  };

  const integerPart = Math.floor(num);
  const fractionalPart = Math.round((num - integerPart) * 100);

  let result = numberToWords(integerPart);
  if (fractionalPart > 0) {
    result += " virgule " + numberToWords(fractionalPart) + " Dirham";
  } else {
    result += " Dirhams";
  }

  return result.replace(/\s\s+/g, " ").trim();
};

const isMobileDevice = () =>
  typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || ""
  );

// --- Main Exported Service Function ---

export const generateCommandPdf = (commandData, companyInfo) => {
  if (!commandData || !companyInfo) {
    throw new Error("Command data and company info are required.");
  }

  const safeCompanyName = (companyInfo.companyLegalName || companyInfo.name || "").toString();
  const safeCapital = formatNumber(companyInfo.capital || 0);
  const bankName = (companyInfo.bankInfo && companyInfo.bankInfo.name) || "";
  const bankRib = (companyInfo.bankInfo && companyInfo.bankInfo.rib) || "";
  const socialAddress =
    (companyInfo.socialInfo && companyInfo.socialInfo.address) || companyInfo.address || "";
  const socialPhone = (companyInfo.socialInfo && companyInfo.socialInfo.phone) || companyInfo.tel || "";
  const socialEmail = (companyInfo.socialInfo && companyInfo.socialInfo.email) || companyInfo.email || "";
  const socialWebsite =
    (companyInfo.socialInfo && companyInfo.socialInfo.website) || companyInfo.website || companyInfo.url || "";
  const taxIds = {
    rc: companyInfo.taxId?.rc || "",
    tp: companyInfo.taxId?.tp || "",
    if: companyInfo.taxId?.if || "",
    cnss: companyInfo.taxId?.cnss || "",
    ice: companyInfo.taxId?.ice || "",
  };

  const clientName = commandData.nameClient || "Client";
  const clientEmail = commandData.emailClient || "";
  const clientAddress = commandData.adressClient || "";
  const clientPhone = commandData.PhoneClient || "";

  const dateObj = new Date(commandData.date_created || new Date());
  const year = dateObj.getFullYear().toString().substr(-2);
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");

  const invoiceNumber = commandData.Nbdevis;
  const invoiceDate = formatDate(commandData.date_created);

  let dueDate = new Date(commandData.date_created || new Date());
  const paymentTerms = commandData.terms_payment || "Règlement à la livraison";
  if (paymentTerms.includes("90 jours"))
    dueDate.setDate(dueDate.getDate() + 90);
  else if (paymentTerms.includes("60 jours"))
    dueDate.setDate(dueDate.getDate() + 60);
  else if (paymentTerms.includes("30 jours"))
    dueDate.setDate(dueDate.getDate() + 30);
  else if (paymentTerms.includes("10 jours"))
    dueDate.setDate(dueDate.getDate() + 10);
  const formattedDueDate = formatDate(dueDate);

  const clientCode = `CU${year}${month}-${String(
    commandData.client_id || 0
  ).padStart(5, "0")}`;
  const commandeId = commandData.commande_id || commandData.id || 0;
  const quoteRef = `TP-CMD${String(commandeId).padStart(6, "0")} / ${invoiceDate}`;
  const clientRef = "CL" + String(commandData.client_id || 0).padStart(6, "0");

  let totalHT = 0;
  let totalTVA = 0;

  const isDelivery = String(commandData.type || '').toLowerCase() === 'bon de livraison';

  let items = Array.isArray(commandData.line) ? commandData.line : [];
  if (!items.length) {
    const fallback = commandData.commande_lines || commandData.ligneCommandes || (commandData.commande && (commandData.commande.ligneCommandes || commandData.commande.lines || commandData.commande.lignes)) || [];
    if (Array.isArray(fallback)) items = fallback;
  }

  let tableBody = [];
  if (isDelivery) {
    const tableHeader = [
      { text: "Désignation", style: "tableHeader" },
      { text: "Type", style: "tableHeader", alignment: "center" },
      { text: "Qté", style: "tableHeader", alignment: "center" },
    ];
    tableBody = [tableHeader];
    if (items.length) {
      items.forEach((item) => {
        const quantity = parseInt(item.quantity || item.quantite) || 0;
        const type = item.item_type || item.type || (item.item_data && item.item_data.type) || '';
        const name = item.nameProduct || item.nameService || (item.item_data && item.item_data.nom) || item.nom_libre || item.nom || 'Produit';
        tableBody.push([
          { text: name, style: "itemName" },
          { text: String(type || '-'), alignment: "center" },
          { text: quantity, alignment: "center" },
        ]);
      });
    } else {
      tableBody.push([
        { text: "Aucun article", style: "itemName", colSpan: 3, alignment: "center" },
        {},
        {},
      ]);
    }
  } else {
    const tableHeader = [
      { text: "Désignation", style: "tableHeader" },
      { text: "TVA", style: "tableHeader", alignment: "center" },
      { text: "P.U. HT", style: "tableHeader", alignment: "center" },
      { text: "Qté", style: "tableHeader", alignment: "center" },
      { text: "Réduc.", style: "tableHeader", alignment: "center" },
      { text: "Total HT", style: "tableHeader", alignment: "center" },
    ];
    tableBody = [tableHeader];
    if (items.length) {
      items.forEach((item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const quantity = parseInt(item.quantity || item.quantite) || 0;
        const itemHT = parseFloat(item.PTHT) || unitPrice * quantity;
        const discount =
          parseFloat(
            item.reduc ||
              item.reduction ||
              item.remise ||
              item.discount ||
              item.discount_percent ||
              item.discountPercentage
          ) || 0;
        totalHT += itemHT;
        
        const itemName = item.nameProduct || item.nameService || (item.item_data && item.item_data.nom) || item.nom_libre || 'Produit';
        
        tableBody.push([
          { text: itemName, style: "itemName" },
          { text: formatPercent(item.tax), alignment: "right", style: "itemText" },
          { text: formatCurrency(unitPrice), alignment: "right", style: "itemText" },
          { text: quantity, alignment: "right", style: "itemText" },
          {
            text: discount ? `${discount}%` : "",
            alignment: "right",
            style: "itemText"
          },
          { text: formatCurrency(itemHT), alignment: "right", style: "itemText" },
        ]);
      });
      totalTVA = items.reduce((sum, item) => {
        const itemHT = parseFloat(item.PTHT) || 0;
        const tax = parseFloat(item.tax) || 0;
        return sum + itemHT * (tax / 100);
      }, 0);
    } else {
      tableBody.push([
        { text: "Aucun article", style: "itemName", colSpan: 6, alignment: "center" },
        {}, {}, {}, {}, {},
      ]);
    }
  }

  const totalTTC = totalHT + totalTVA;
  const totalTTCWords = convertToWords(totalTTC) + " Dirhams toutes taxes comprises";
  const documentTitle =
    commandData.type === "devis"
      ? "Devis"
      : commandData.type === "facture"
      ? "Facture"
      : "Bon de livraison";

  const tableWidths = isDelivery
    ? ["*", "auto", "auto"]
    : ["*", 50, 70, 45, 50, 70];

  const validityDate = formatDate(commandData.valide_jusqua || commandData.valid_until || formattedDueDate);

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [40, 40, 40, 90],
    content: [
      // Header section
      {
        columns: [
          companyInfo && companyInfo.logoSmall
            ? {
                width: 120,
                image: companyInfo.logoSmall,
                fit: [120, 80],
                alignment: "left",
              }
            : { 
                width: 120, 
                text: companyInfo.name || safeCompanyName,
                style: "companyNameHeader",
                alignment: "left"
              },
          {
            width: "*",
            stack: [
              { 
                text: `${documentTitle} ${invoiceNumber}`, 
                style: "documentTitle", 
                alignment: "right",
                margin: [0, 0, 0, 8]
              },
              {
                text: `Date : ${invoiceDate}`,
                style: "headerInfo",
                alignment: "right"
              },
              {
                text: `Date de fin de validité : ${validityDate}`,
                style: "headerInfo",
                alignment: "right"
              },
              {
                text: `Code client : ${clientCode}`,
                style: "headerInfo",
                alignment: "right"
              },
              ...(!isDelivery
                ? [
                    {
                      text: `Réf. client : ${clientRef}`,
                      style: "headerInfo",
                      alignment: "right",
                    },
                  ]
                : []),
              {
                text: `${isDelivery ? "Réf. Facture" : "Réf. Command"} : ${isDelivery ? commandData.facture_ref || quoteRef : quoteRef}`,
                style: "headerInfo",
                alignment: "right",
              },
            ],
          },
        ],
        columnGap: 20,
        margin: [0, 0, 0, 20],
      },

      // Emetteur and Adressé à section
      {
        columns: [
          {
            width: "48%",
            stack: [
              { text: "Émetteur", style: "sectionTitle", margin: [0, 0, 0, 5] },
              {
                table: {
                  widths: ["*"],
                  heights: [80],
                  body: [
                    [
                      {
                        stack: [
                          { text: companyInfo.name || safeCompanyName, bold: true, fontSize: 10 },
                          { text: socialAddress, fontSize: 9, margin: [0, 2, 0, 2] },
                          { text: `Email: ${socialEmail}`, fontSize: 9 },
                          { text: `Web: ${socialWebsite}`, fontSize: 9 },
                        ],
                        margin: [8, 8, 8, 8],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 1,
                  vLineWidth: () => 1,
                  hLineColor: () => "#cccccc",
                  vLineColor: () => "#cccccc",
                },
              },
            ],
          },
          {
            width: "48%",
            stack: [
              { text: "Adressé à", style: "sectionTitle", margin: [0, 0, 0, 5] },
              {
                table: {
                  widths: ["*"],
                  heights: [80],
                  body: [
                    [
                      {
                        stack: [
                          { text: clientName, bold: true, fontSize: 10 },
                          { text: clientAddress, fontSize: 9, margin: [0, 2, 0, 2] },
                          { text: `Tél: ${clientPhone}`, fontSize: 9 },
                          { text: `Email: ${clientEmail}`, fontSize: 9 },
                        ],
                        margin: [8, 8, 8, 8],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 1,
                  vLineWidth: () => 1,
                  hLineColor: () => "#cccccc",
                  vLineColor: () => "#cccccc",
                },
              },
            ],
          },
        ],
        columnGap: 20,
        margin: [0, 0, 0, 15],
      },

      // Category if exists
      ...(commandData.categorie_operation
        ? [
            {
              text: `Catégorie d'opérations : ${commandData.categorie_operation}`,
              fontSize: 9,
              margin: [0, 0, 0, 10],
            },
          ]
        : []),

      // Montants label
      {
        text: "Montants exprimés en Dirham",
        fontSize: 9,
        italics: true,
        alignment: "right",
        margin: [0, 0, 0, 8],
      },

      // Main table
      {
        table: {
          headerRows: 1,
          widths: tableWidths,
          body: tableBody,
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.5,
          vLineWidth: () => 1,
          hLineColor: () => "#cccccc",
          vLineColor: () => "#cccccc",
          paddingLeft: () => 8,
          paddingRight: () => 8,
          paddingTop: () => 6,
          paddingBottom: () => 6,
        },
        margin: [0, 0, 0, 15],
      },

      // Totals and summary section
      ...(isDelivery
        ? [
            {
              margin: [0, 20, 0, 0],
              columns: [
                { width: "*", text: "" },
                {
                  width: 200,
                  table: {
                    widths: ["*"],
                    body: [
                      [{ text: "Cachet, Date, Signature et mention \"Bon pour Accord\"", fontSize: 9, alignment: "center", margin: [5, 5, 5, 35] }],
                    ],
                  },
                  layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => "#cccccc",
                    vLineColor: () => "#cccccc",
                  },
                },
              ],
            },
          ]
        : [
            {
              columns: [
                {
                  width: "*",
                  text: `Arrêté la présente ${documentTitle.toLowerCase()} à la somme de : ${totalTTCWords}`,
                  fontSize: 9,
                  italics: true,
                },
                {
                  width: 200,
                  table: {
                    widths: ["*", "auto"],
                    body: [
                      [
                        { text: "Total HT", alignment: "right", fontSize: 10 },
                        { text: formatCurrency(totalHT), alignment: "right", fontSize: 10 },
                      ],
                      [
                        {
                          text: `Total TVA ${items.length > 0 && items[0].tax ? formatPercent(items[0].tax) : "20%"}`,
                          alignment: "right",
                          fontSize: 10,
                        },
                        { text: formatCurrency(totalTVA), alignment: "right", fontSize: 10 },
                      ],
                      [
                        { text: "Total TTC", alignment: "right", bold: true, fontSize: 11 },
                        { text: formatCurrency(totalTTC), alignment: "right", bold: true, fontSize: 11 },
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: (i, node) => (i === node.table.body.length) ? 1 : 0,
                    vLineWidth: () => 0,
                    paddingLeft: () => 8,
                    paddingRight: () => 8,
                    paddingTop: () => 4,
                    paddingBottom: () => 4,
                  },
                },
              ],
              margin: [0, 0, 0, 15],
            },

            // Payment info and signature
            {
              columns: [
                {
                  width: "*",
                  stack: [
                    {
                      text: `Règlement TTC par chèque à l'ordre de ${companyInfo.name || safeCompanyName} envoyé à`,
                      fontSize: 9,
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text: socialAddress,
                      fontSize: 9,
                      bold: true,
                    },
                  ],
                  table: {
                    widths: ["*"],
                    body: [[{ stack: [
                      {
                        text: `Règlement TTC par chèque à l'ordre de ${companyInfo.name || safeCompanyName} envoyé à`,
                        fontSize: 9,
                      },
                      {
                        text: socialAddress,
                        fontSize: 9,
                        bold: true,
                        margin: [0, 2, 0, 0],
                      },
                    ], margin: [8, 8, 8, 8] }]],
                  },
                  layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => "#cccccc",
                    vLineColor: () => "#cccccc",
                  },
                },
                {
                  width: 200,
                  table: {
                    widths: ["*"],
                    body: [
                      [{ text: "Cachet, Date, Signature et mention \"Bon pour Accord\"", fontSize: 9, alignment: "center", margin: [5, 5, 5, 35] }],
                    ],
                  },
                  layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => "#cccccc",
                    vLineColor: () => "#cccccc",
                  },
                },
              ],
              columnGap: 15,
              margin: [0, 10, 0, 0],
            },
          ]),
    ],
    styles: {
      companyNameHeader: {
        fontSize: 14,
        bold: true,
      },
      documentTitle: { 
        fontSize: 14, 
        bold: true,
      },
      headerInfo: {
        fontSize: 9,
        margin: [0, 1, 0, 1],
      },
      sectionTitle: { 
        fontSize: 10, 
        bold: false,
      },
      tableHeader: { 
        fontSize: 10, 
        bold: true, 
        fillColor: "#f0f0f0",
        margin: [0, 6, 0, 6],
      },
      itemName: { 
        fontSize: 9,
        bold: true,
        lineHeight: 1.2,
      },
      itemText: {
        fontSize: 10,
      },
    },
    defaultStyle: { 
      fontSize: 10,
      lineHeight: 1.2,
    },
    footer: (currentPage, pageCount) => {
      return {
        margin: [40, 10, 40, 16],
        fontSize: 8,
        color: "#666666",
        stack: [
          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 0.5,
                lineColor: "#cccccc",
              },
            ],
            margin: [0, 0, 0, 8],
          },
          {
            columns: [
              {
                width: "*",
                stack: [
                  {
                    text: `${safeCompanyName ? safeCompanyName : ""} | Capital ${safeCapital} Dirhams`,
                    alignment: "center",
                  },
                  {
                    text: `${bankName ? bankName + " - " : ""}RIB : ${bankRib} | Tél : ${socialPhone} | ${socialEmail} | ${socialWebsite}`,
                    alignment: "center",
                  },
                  {
                    text: `RC : ${taxIds.rc} | PT N° : ${taxIds.if} | IF : ${taxIds.tp} | CNSS : ${taxIds.cnss} | ICE : ${taxIds.ice}`,
                    alignment: "center",
                  },
                ],
              },
            ],
          },
          { text: `${currentPage}/${pageCount}`, alignment: "right", margin: [0, 4, 0, 0] },
        ],
      };
    },
  };

  try {
    const pdfDoc = pdfMake.createPdf(docDefinition);
    const dateSegment = `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, "0")}${String(dateObj.getDate()).padStart(2, "0")}`;
    const isUuidLike = typeof invoiceNumber === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(invoiceNumber);
    const numericId = commandData.id || commandData.commande_id || commandData.client_id || 1;
    const fallbackNumber = `${documentTitle}-${dateSegment}-${String(numericId).padStart(4, "0")}`;
    const safeNumber = invoiceNumber && !isUuidLike ? invoiceNumber : fallbackNumber;
    const sanitizedNumber = String(safeNumber).replace(/\s+/g, "").replace(/[^A-Za-z0-9_-]/g, "-");
    const filename = `${sanitizedNumber}.pdf`;

    if (isMobileDevice()) {
      pdfDoc.getBlob((blob) => {
        try {
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        } catch (err) {
          console.error("Mobile PDF download failed, falling back to open()", err);
          pdfDoc.open();
        }
      });
    } else {
      // Desktop: open the PDF in a new tab/window (filename is applied if user chooses to download there)
      pdfDoc.open();
    }
  } catch (error) {
    console.error("Error during PDF generation:", error);
    throw new Error("An error occurred while creating the PDF document.");
  }
};
